'use client';

import { twMerge } from '@shared/themes';
import Image, { type ImageProps } from 'next/image';

type ThemeAwareImageProps = Omit<ImageProps, 'src'> & {
  src: string | { light: string; dark: string };
  alt: string;
};

/**
 * Renders an image that automatically changes based on the current theme (light or dark).
 * The `src` prop accepts either a single image URL (string) or an object with separate `light` and `dark` URLs.
 * If an object is provided, both variants are rendered and visibility is toggled via CSS (`dark:hidden` / `hidden dark:block`),
 * matching the pattern used in `global-error.tsx` and gamble-safely static pages.
 */
function ThemeAwareImage({ src, alt, className, ...props }: ThemeAwareImageProps) {
  if (typeof src === 'string') {
    return <Image src={src} alt={alt} className={className} {...props} />;
  }

  return (
    <>
      <Image src={src.light} alt={alt} className={twMerge(className, 'dark:hidden')} {...props} />
      <Image src={src.dark} alt={alt} className={twMerge(className, 'hidden dark:block')} {...props} />
    </>
  );
}

export { ThemeAwareImage };
